import { graphql } from 'gatsby';
import RichTextBlock from '@/components/RichTextBlock/RichTextBlock';
import { ImageElement } from '@kontent-ai/gatsby-components';
import StarRating from '@/components/StarRating/StarRating';
import NavigationDropdown from '@/components/NavigationDropdown/NavigationDropdown';
import {
  StyledArticle,
  StyledButton,
  StyledDescription,
  StyledEyebrow,
  StyledHeadline,
  StyledIcon,
  StyledImageContainer,
  StyledContent,
  StyledInfoTag,
} from './N5050BlockTextItem.styles';

type N5050BlockTextItemProps = {
  n5050BlockTextItemElements: Queries.N5050_BLOCK_TEXT_ITEMFragment['n5050BlockTextItemElements'];
  isCampaignForm?: boolean;
};

const N5050BlockTextItem = ({
  n5050BlockTextItemElements: elements,
  isCampaignForm = false,
}: N5050BlockTextItemProps) => {
  const { eyebrow, headline, description, cta, image, icon, star_rating, tag } = elements;
  const eyebrowValue = eyebrow?.value;
  const headlineValue = headline.value;
  const ctaValue = cta?.value;
  const hasIcon = !!icon?.value;
  const hasStarRating = !!star_rating?.value;
  const imageValue = image?.value && {
    ...image.value,
    url: image.value.url,
    description: image.value.description ?? '',
    height: image.value.height!,
    width: image.value.width!,
  };
  const hasInfoTag = !!tag?.value;

  return (
    <StyledArticle>
      {imageValue && (
        <StyledImageContainer>
          <ImageElement
            alt={imageValue.description}
            height={imageValue.height}
            image={{ ...imageValue, image_id: imageValue.url }}
            width={imageValue.width}
          />
        </StyledImageContainer>
      )}
      {hasIcon && <StyledIcon {...icon.value} />}
      {hasInfoTag && <StyledInfoTag {...tag.value} />}
      {eyebrowValue && <StyledEyebrow>{eyebrowValue}</StyledEyebrow>}
      <StyledContent isCampaignForm={isCampaignForm}>
        <StyledHeadline>{headlineValue}</StyledHeadline>
        <StyledDescription>
          <RichTextBlock
            elements={{
              rich_text_content: description,
            }}
          />
        </StyledDescription>
      </StyledContent>
      {hasStarRating && <StarRating {...star_rating.value} />}
      {ctaValue?.__typename &&
        (ctaValue.__typename === 'kontent_item_link' ? (
          <StyledButton {...ctaValue} variant="tertiary" />
        ) : (
          <NavigationDropdown {...ctaValue} buttonWidthFull={false} />
        ))}
    </StyledArticle>
  );
};
export default N5050BlockTextItem;

export const fragment = graphql`
  fragment N5050_BLOCK_TEXT_ITEM on kontent_item_n5050_block_text_item {
    n5050BlockTextItemElements: elements {
      icon {
        value {
          url
        }
      }
      image {
        value {
          width
          url
          type
          height
          name
          description
        }
      }
      eyebrow {
        value
      }
      tag {
        value {
          ...INFO_TAG
        }
      }
      headline {
        value
      }
      description {
        value
        images {
          description
          height
          url
          image_id
          width
        }
        links {
          link_id
          codename
        }
        modular_content {
          ...CODE_SNIPPET
          ...TABLE
          ...VIDEO
          ...QUOTE
        }
      }
      cta {
        value {
          __typename
          ... on kontent_item {
            system {
              id
            }
          }
          ...LINK
          ...NAVIGATION_DROPDOWN
        }
      }
      star_rating {
        value {
          ...STAR_RATING
        }
      }
    }
  }
`;
